/**
 * Graph structural invariant checks for the journey builder.
 *
 * ⚠️  HERE BE DRAGONS ⚠️
 *
 * This file validates the 6 core graph invariants that keep the journey DAG
 * well-formed. These invariants are enforced at two levels:
 *
 *   1. Mutation-time checks (checkScopeNesting, checkJoinDominance) — called
 *      by graph-mutations.ts before inserting branching nodes
 *   2. Full-graph validation (validateGraphInvariants) — safety-net pass that
 *      checks all invariants across every branching node in the graph
 *
 * The invariants cover: path convergence, branch exclusivity, join reachability,
 * scope nesting, shared join targets, join dominance, and placeholder integrity.
 *
 * WHEN TO MODIFY THIS FILE:
 *   - Adding or changing a graph invariant
 *   - Fixing false positives/negatives in invariant checks
 *
 * WHEN NOT TO MODIFY THIS FILE:
 *   - Adding a new step type → update step-factory.ts and step-config.tsx
 *   - Changing graph mutation logic → update graph-mutations.ts
 *   - Changing field-level validation → update graph/validation.ts
 */
import type { JourneyNode, JourneyEdge } from '../../types/journey-nodes'
import { isPlaceholderJourneyNode } from '../../types/journey-nodes'
import { isBranchingNode, getBranchingNodeData } from './traversal'
import { getNodeOutStepId } from './traversal'
import { ScopeCache } from './scope'
import { StepType } from '@pushly/models/lib/enums/journeys'
import { graphHasMeaningfulSteps, isEmptyPlaceholder } from '../../types/journey-nodes'

/**
 * Structured error returned when a mutation violates a graph invariant.
 * Mutations return this instead of modifying the graph.
 */
export interface MutationError {
    invariantId: string
    message: string
    offendingNodeIds?: string[]
    context?: Record<string, unknown>
}

/**
 * Invariant IDs matching the 6 graph invariants from requirements.
 */
export const InvariantId = {
    PATH_CONVERGENCE: 'PATH_CONVERGENCE',
    BRANCH_EXCLUSIVITY: 'BRANCH_EXCLUSIVITY',
    JOIN_REACHABILITY: 'JOIN_REACHABILITY',
    SCOPE_NESTING: 'SCOPE_NESTING',
    SHARED_JOIN_TARGETS: 'SHARED_JOIN_TARGETS',
    JOIN_DOMINANCE: 'JOIN_DOMINANCE',
    PLACEHOLDER_DELETE_BLOCKED: 'PLACEHOLDER_DELETE_BLOCKED',
    PLACEHOLDER_OWNERSHIP: 'PLACEHOLDER_OWNERSHIP',
    PLACEHOLDER_LINEAR_INTEGRITY: 'PLACEHOLDER_LINEAR_INTEGRITY',
    PLACEHOLDER_ROLE_REQUIRED: 'PLACEHOLDER_ROLE_REQUIRED',
    PLACEHOLDER_EMPTY_POSITION: 'PLACEHOLDER_EMPTY_POSITION',
    PLACEHOLDER_EMPTY_UNIQUENESS: 'PLACEHOLDER_EMPTY_UNIQUENESS',
    PLACEHOLDER_EMPTY_EXCLUSIVITY: 'PLACEHOLDER_EMPTY_EXCLUSIVITY',
} as const

/**
 * Check Scope Nesting (Invariant 4):
 * When inserting a branching node C inside parent scope P,
 * C's join_step_id must be within P's scope boundary.
 *
 * For each parent branching node, get parent scope and parent join ID.
 * join_step_id must be within parent scope OR be the parent's own join target.
 * If not, return MutationError with invariantId: SCOPE_NESTING.
 */
export function checkScopeNesting(
    newJoinStepId: string,
    parentBranchingNodes: JourneyNode[],
    nodeMap: Map<string, JourneyNode>,
    scopeCache: ScopeCache,
): MutationError | null {
    for (const parent of parentBranchingNodes) {
        const parentScope = scopeCache.getScopeNodes(parent.id, nodeMap)
        const parentData = getBranchingNodeData(parent)
        const parentJoinId = parentData?.join_step_id != null ? String(parentData.join_step_id) : null

        if (!parentScope.has(newJoinStepId) && newJoinStepId !== parentJoinId) {
            return {
                invariantId: InvariantId.SCOPE_NESTING,
                message: `Branching node's join target must be within the enclosing branch scope`,
                offendingNodeIds: [parent.id],
            }
        }
    }
    return null
}

/**
 * Check Join Dominance (Invariant 6) at mutation time:
 * When inserting a branching node C inside parent scope P,
 * verify that C's join does not interleave with P's scope structure.
 *
 * For each parent branching node, get parent scope.
 * Check all existing branching nodes in parent's scope.
 * If a sibling branching node's scope contains the new join but NOT the
 * insertion source, that creates interleaving.
 *
 * ASSUMPTION: insertionEdgeSourceId correctly identifies the branch
 * context — i.e., parentBranchingNodes are the actual enclosing scopes
 * and the source edge identifies which branch the new node is being
 * inserted into. This holds because getAddNodeMutations computes
 * enclosingBranchingNodes from the source node before calling this check.
 */
export function checkJoinDominance(
    newNodeId: string,
    newJoinStepId: string,
    insertionEdgeSourceId: string,
    parentBranchingNodes: JourneyNode[],
    nodeMap: Map<string, JourneyNode>,
    scopeCache: ScopeCache,
): MutationError | null {
    for (const parent of parentBranchingNodes) {
        const parentScope = scopeCache.getScopeNodes(parent.id, nodeMap)

        for (const scopeNodeId of parentScope) {
            const scopeNode = nodeMap.get(scopeNodeId)
            if (!scopeNode || !isBranchingNode(scopeNode)) continue
            const siblingData = getBranchingNodeData(scopeNode)
            if (!siblingData || siblingData.join_step_id == null) continue

            const siblingScope = scopeCache.getScopeNodes(scopeNodeId, nodeMap)
            if (siblingScope.has(newJoinStepId) && !siblingScope.has(insertionEdgeSourceId)) {
                return {
                    invariantId: InvariantId.JOIN_DOMINANCE,
                    message: `Inserting this branching step would create interleaving scopes with an existing branching step`,
                    offendingNodeIds: [newNodeId, scopeNodeId],
                }
            }
        }
    }
    return null
}

/**
 * Walk from startId collecting nodes, stopping at stopId.
 * When encountering a nested branching node, recurse into its branches
 * and continue from its join_step_id.
 *
 * Used internally by validateGraphInvariants for per-branch scope
 * computation (Branch Exclusivity check). This mirrors the walkScope
 * logic in scope.ts but is local to avoid coupling to a
 * private function.
 */
function walkBranchScope(
    startId: string,
    stopId: string | null,
    nodeMap: Map<string, JourneyNode>,
    scope: Set<string>,
): void {
    let currentId: string | null = startId

    while (currentId != null) {
        if (currentId === stopId) break
        if (scope.has(currentId)) break

        const node = nodeMap.get(currentId)
        if (!node) break

        scope.add(currentId)

        if (isBranchingNode(node)) {
            const nested = getBranchingNodeData(node)
            if (nested) {
                for (const branch of nested.branches) {
                    if (branch.out_step_id == null) continue
                    const branchStartId = String(branch.out_step_id)
                    if (branchStartId === stopId) continue
                    walkBranchScope(branchStartId, stopId, nodeMap, scope)
                }
                const nestedJoinId = nested.join_step_id != null ? String(nested.join_step_id) : null
                if (nestedJoinId === stopId) break
                currentId = nestedJoinId
                continue
            }
        }

        const outStepId = getNodeOutStepId(node)
        currentId = outStepId
    }
}

/**
 * Walk from startId following out_step_id chains. When hitting a branching
 * node, jump to its join_step_id. Return true if we reach targetId (the
 * join) or an EXIT node. Return false if we hit a dead end (no out_step_id
 * and not EXIT).
 */
function pathReachesTarget(
    startId: string,
    targetId: string | null,
    nodeMap: Map<string, JourneyNode>,
): boolean {
    const visited = new Set<string>()
    let currentId: string | null = startId

    while (currentId != null) {
        if (currentId === targetId) return true
        if (visited.has(currentId)) return false

        const node = nodeMap.get(currentId)
        if (!node) return false

        visited.add(currentId)

        // EXIT node is a valid termination
        if (node.data.step.type === StepType.EXIT) return true

        if (isBranchingNode(node)) {
            const data = getBranchingNodeData(node)
            if (data) {
                // Jump through nested branching node to its join
                currentId = data.join_step_id != null ? String(data.join_step_id) : null
                continue
            }
        }

        currentId = getNodeOutStepId(node)
    }

    // Dead end — no out_step_id and not EXIT
    return false
}

/**
 * Validate all 6 graph invariants for every branching node in the graph.
 * Used as a safety-net validation pass (mutations enforce at write-time).
 *
 * Algorithm:
 * 1. Build a nodeMap from the node list.
 * 2. For each branching node B:
 *    a. Minimum branch count: B.branches.length >= 1, else error.
 *    b. Path Convergence: each branch must reach join or EXIT.
 *    c. Branch Exclusivity: no node in more than one branch's reachable set.
 *    d. Join Reachability: B.id must not be in its own scope (cycle check).
 *    e. Scope Nesting + Join Dominance: nested branching nodes' joins must
 *       be within scopeNodes(B) ∪ {B.join_step_id}.
 * 3. Collect and return all MutationError instances.
 */
export function validateGraphInvariants(
    nodes: JourneyNode[],
    edges: JourneyEdge[],
    scopeCache: ScopeCache,
): MutationError[] {
    const nodeMap = new Map(nodes.map((n) => [n.id, n]))
    const errors: MutationError[] = []

    for (const node of nodes) {
        if (!isBranchingNode(node)) continue
        const data = getBranchingNodeData(node)
        if (!data) continue

        // Minimum branch count
        if (data.branches.length < 1) {
            errors.push({
                invariantId: InvariantId.PATH_CONVERGENCE,
                message: 'Branching node must have at least one branch',
                offendingNodeIds: [node.id],
            })
            continue
        }

        const joinId = data.join_step_id != null ? String(data.join_step_id) : null
        const scope = scopeCache.getScopeNodes(node.id, nodeMap)

        // Path Convergence: each branch must reach joinId or EXIT
        for (const branch of data.branches) {
            if (branch.out_step_id == null) continue
            if (!pathReachesTarget(String(branch.out_step_id), joinId, nodeMap)) {
                errors.push({
                    invariantId: InvariantId.PATH_CONVERGENCE,
                    message: `Branch '${branch.key}' does not reach the convergence point or an exit`,
                    offendingNodeIds: [node.id],
                    context: { branchKey: branch.key },
                })
            }
        }

        // Branch Exclusivity: no node in more than one branch's reachable set
        const branchSets = new Map<string, Set<string>>()
        for (const branch of data.branches) {
            if (branch.out_step_id == null) continue
            const reachable = new Set<string>()
            walkBranchScope(String(branch.out_step_id), joinId, nodeMap, reachable)
            branchSets.set(branch.key, reachable)
        }
        const seen = new Map<string, string>()
        for (const [branchKey, reachable] of branchSets) {
            for (const nid of reachable) {
                const existing = seen.get(nid)
                if (existing && existing !== branchKey) {
                    errors.push({
                        invariantId: InvariantId.BRANCH_EXCLUSIVITY,
                        message: `Node ${nid} is reachable from both branch '${existing}' and '${branchKey}'`,
                        offendingNodeIds: [node.id, nid],
                    })
                }
                seen.set(nid, branchKey)
            }
        }

        // Join Reachability: no cycles (B not in its own scope)
        if (joinId && scope.has(node.id)) {
            errors.push({
                invariantId: InvariantId.JOIN_REACHABILITY,
                message: 'Branching node is within its own scope (cycle detected)',
                offendingNodeIds: [node.id],
            })
        }

        // Scope Nesting + Join Dominance: nested branching nodes' joins
        // must be in scopeNodes(B) ∪ {B.join_step_id}
        for (const scopeNodeId of scope) {
            const scopeNode = nodeMap.get(scopeNodeId)
            if (!scopeNode || !isBranchingNode(scopeNode)) continue
            const nestedData = getBranchingNodeData(scopeNode)
            if (!nestedData || nestedData.join_step_id == null) continue
            const nestedJoinId = String(nestedData.join_step_id)
            const allowedSet = new Set([...scope, ...(joinId ? [joinId] : [])])
            if (!allowedSet.has(nestedJoinId)) {
                errors.push({
                    invariantId: InvariantId.JOIN_DOMINANCE,
                    message: `Nested branching node ${scopeNodeId} has join target ${nestedJoinId} outside enclosing scope`,
                    offendingNodeIds: [node.id, scopeNodeId],
                })
            }
        }
    }

    // Placeholder Ownership: for each branching node, if its join target is a
    // PLACEHOLDER node with placeholderRole === 'convergence', validate ownership matches.
    // Legacy branching nodes (join target has no placeholderRole marker) are exempt.
    for (const node of nodes) {
        if (!isBranchingNode(node)) continue
        const data = getBranchingNodeData(node)
        if (!data || data.join_step_id == null) continue

        const joinNode = nodeMap.get(String(data.join_step_id))
        if (!joinNode) continue

        // Only validate convergence placeholder nodes (post-feature)
        if (
            joinNode.data.step.type === StepType.PLACEHOLDER &&
            joinNode.data.uiMeta?.placeholderRole === 'convergence'
        ) {
            if (joinNode.data.uiMeta.convergenceOf !== node.id) {
                errors.push({
                    invariantId: InvariantId.PLACEHOLDER_OWNERSHIP,
                    message: `Placeholder node ${joinNode.id} is owned by ${joinNode.data.uiMeta.convergenceOf} but referenced by ${node.id}`,
                    offendingNodeIds: [node.id, joinNode.id],
                })
            }
        }
    }

    // PLACEHOLDER linear integrity: must have exactly one valid out_step_id
    for (const node of nodes) {
        if (!isPlaceholderJourneyNode(node)) continue
        const outStepId = node.data.step.configuration?.out_step_id
        if (outStepId == null || !nodeMap.has(String(outStepId))) {
            errors.push({
                invariantId: InvariantId.PLACEHOLDER_LINEAR_INTEGRITY,
                message: `Placeholder node ${node.id} has invalid or missing out_step_id: ${outStepId}`,
                offendingNodeIds: [node.id],
            })
        }
    }

    // PLACEHOLDER invariants
    for (const node of nodes) {
        if (node.data.step.type !== StepType.PLACEHOLDER) continue
        const role = node.data.uiMeta?.placeholderRole

        // Must have exactly one valid role (orphans have undefined role + invalid flag)
        if (role !== 'empty' && role !== 'convergence') {
            errors.push({
                invariantId: InvariantId.PLACEHOLDER_ROLE_REQUIRED,
                message: `Placeholder node ${node.id} has invalid or missing placeholderRole: ${role}`,
                offendingNodeIds: [node.id],
            })
        }

        // Empty placeholder structural position: must be on the main chain
        // between entry (TRIGGER/GLOBAL_TRIGGER) and exit (EXIT)
        if (role === 'empty') {
            const inboundEdges = edges.filter((e) => e.target === node.id)
            const outboundEdges = edges.filter((e) => e.source === node.id)
            const inboundSources = inboundEdges.map((e) => nodeMap.get(e.source))
            const outboundTargets = outboundEdges.map((e) => nodeMap.get(e.target))

            const hasValidInbound = inboundSources.length === 1
                && inboundSources[0] != null
                && [StepType.TRIGGER, StepType.GLOBAL_TRIGGER].includes(inboundSources[0].data.step.type)
            const hasValidOutbound = outboundTargets.length === 1
                && outboundTargets[0] != null
                && outboundTargets[0].data.step.type === StepType.EXIT

            if (!hasValidInbound || !hasValidOutbound) {
                errors.push({
                    invariantId: InvariantId.PLACEHOLDER_EMPTY_POSITION,
                    message: `Empty placeholder ${node.id} must be directly between entry and exit nodes`,
                    offendingNodeIds: [node.id],
                })
            }
        }
    }

    // Empty placeholder exclusivity: at most one, and only when no meaningful steps exist
    const emptyPlaceholders = nodes.filter(
        (n) => isEmptyPlaceholder(n)
    )
    const hasMeaningful = graphHasMeaningfulSteps(nodes)

    if (emptyPlaceholders.length > 1) {
        errors.push({
            invariantId: InvariantId.PLACEHOLDER_EMPTY_UNIQUENESS,
            message: `Graph contains ${emptyPlaceholders.length} empty placeholders (max 1)`,
            offendingNodeIds: emptyPlaceholders.map((n) => n.id),
        })
    }

    if (hasMeaningful && emptyPlaceholders.length > 0) {
        errors.push({
            invariantId: InvariantId.PLACEHOLDER_EMPTY_EXCLUSIVITY,
            message: 'Graph contains meaningful steps but still has an empty placeholder',
            offendingNodeIds: emptyPlaceholders.map((n) => n.id),
        })
    }

    return errors
}
